import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { IBringBackItem, IStorageState } from "./types";
import { endpoints } from "../../../services/origin/storageApi/storageApi";
import { ViewType } from "../../../views/Storages/components/StorageList/components/StorageListHeader/types";

export const storageInitialState: IStorageState = {
  storageViewType: ViewType.GRID,
  selectedStorageIds: [],
};

const getMaxBringBackQty = (qty: number, requestedOpenQty?: number, remainingQty?: number) => {
  const resolvedRemaining =
    remainingQty !== undefined && remainingQty !== null
      ? remainingQty
      : qty - (requestedOpenQty ?? 0);
  return Math.max(0, Math.min(qty, resolvedRemaining));
};

const getSanitizedCartQty = (item: IBringBackItem) => {
  const qty = item.storage.qty ?? 0;
  const maxQty = getMaxBringBackQty(
    qty,
    item.storage.requestedOpenQty,
    item.storage.remainingQty
  );
  return Math.max(0, Math.min(item.cartQty, maxQty));
};

const storageSlice = createSlice({
  name: "storage",
  initialState: storageInitialState,
  reducers: {
    addBringBackItem: (state, { payload }: PayloadAction<IBringBackItem>) => {
      const sanitizedCartQty = getSanitizedCartQty(payload);
      const sanitizedPayload = {
        ...payload,
        cartQty: sanitizedCartQty,
      };

      console.log("[storageSlice] addBringBackItem", {
        storageId: payload.storage.id,
        incomingCartQty: payload.cartQty,
        sanitizedCartQty,
        qty: payload.storage.qty,
        requestedOpenQty: payload.storage.requestedOpenQty,
        remainingQty: payload.storage.remainingQty,
      });

      if (state.bringBackCart) {
        const isInCartIndex = state.bringBackCart.findIndex(
          (item) => item.storage.id === payload.storage.id
        );

        if (isInCartIndex !== -1) {
          if (sanitizedCartQty === 0) {
            state.bringBackCart.splice(isInCartIndex, 1);
          } else {
            state.bringBackCart[isInCartIndex].cartQty = sanitizedCartQty;
          }
        } else {
          if (sanitizedCartQty !== 0) {
            state.bringBackCart.push(sanitizedPayload);
          }
        }
      } else {
        if (sanitizedCartQty !== 0) {
          state.bringBackCart = [sanitizedPayload];
        }
      }
    },
    clearBringBackCart: (state) => {
      state.bringBackCart = undefined;
    },
    addAllItemsToBringBackCart: (state, { payload }: PayloadAction<IBringBackItem[]>) => {
      console.log("[storageSlice] addAllItemsToBringBackCart", {
        itemCount: payload.length,
        existingCartLength: state.bringBackCart?.length ?? 0,
      });
      if (state.bringBackCart) {
        // Add all items that are not already in the cart
        payload.forEach((newItem) => {
          const sanitizedCartQty = getSanitizedCartQty(newItem);
          console.log("[storageSlice] addAll item sanitized", {
            storageId: newItem.storage.id,
            incomingCartQty: newItem.cartQty,
            sanitizedCartQty,
            qty: newItem.storage.qty,
            requestedOpenQty: newItem.storage.requestedOpenQty,
            remainingQty: newItem.storage.remainingQty,
          });
          if (sanitizedCartQty === 0) {
            return;
          }

          const isInCartIndex = state.bringBackCart!.findIndex(
            (item) => item.storage.id === newItem.storage.id
          );
          if (isInCartIndex === -1) {
            state.bringBackCart!.push({
              ...newItem,
              cartQty: sanitizedCartQty,
            });
          }
        });
      } else {
        const sanitizedPayload = payload
          .map((item) => ({
            ...item,
            cartQty: getSanitizedCartQty(item),
          }))
          .filter((item) => item.cartQty > 0);

        console.log("[storageSlice] addAll sanitized payload", {
          originalCount: payload.length,
          sanitizedCount: sanitizedPayload.length,
          sanitizedPayload,
        });

        if (sanitizedPayload.length > 0) {
          state.bringBackCart = sanitizedPayload;
        }
      }
    },
    setStorageViewType: (state, { payload }: PayloadAction<ViewType>) => {
      state.storageViewType = payload;
    },
    toggleSelectedStorage: (state, { payload }: PayloadAction<number>) => {
      const selectedIndex = state.selectedStorageIds.findIndex(
        (id) => id === payload,
      );
      if (selectedIndex === -1) {
        state.selectedStorageIds.push(payload);
        return;
      }
      state.selectedStorageIds.splice(selectedIndex, 1);
    },
    setSelectedStorages: (state, { payload }: PayloadAction<number[]>) => {
      state.selectedStorageIds = Array.from(new Set(payload));
    },
    clearSelectedStorages: (state) => {
      state.selectedStorageIds = [];
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      endpoints.getStoragesRequest.matchFulfilled,
      (state, { payload }) => {
        state.storages = payload;
      }
    );
  },
});

export const {
  addBringBackItem,
  setStorageViewType,
  clearBringBackCart,
  addAllItemsToBringBackCart,
  toggleSelectedStorage,
  setSelectedStorages,
  clearSelectedStorages,
} = storageSlice.actions;
export default storageSlice.reducer;
